import type React from 'react';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import ProfileCompleteImage from 'app/assets/leads/profile-complete-preview.png';
import ProfileIncompleteImage from 'app/assets/leads/profile-incomplete-preview.png';
import { getGuideColors } from 'app/features/leads/components/guide/guide-colors';
import {
  CircleCheckIcon,
  CircleXCloseIcon,
  ChevronDownSmall,
  ChevronUpSmall,
} from 'app/features/leads/components/guide/guide-icons';
import Typography from 'app/ui/components/atoms/typography';

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(5)};
  width: 100%;
  flex-shrink: 0;
`;

const Subtitle = styled(Typography)`
  text-align: center;
`;

const PROFILE_PREVIEW_EXPANDED_HEIGHT = '33rem';
const PROFILE_PREVIEW_COLLAPSED_HEIGHT = '10rem';
const PROFILE_PREVIEW_WIDTH = '17.3125rem';

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme: { spacing } }) => spacing(5)};
  width: 100%;
  flex-shrink: 0;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      grid-template-columns: repeat(2, ${PROFILE_PREVIEW_WIDTH});
      justify-content: center;
      column-gap: ${spacing(6)};
      align-items: start;
    `,
    )}
`;

const ComparisonCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(5)};
  width: 100%;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.semantic.neutrals_100};
  border-radius: 0.75rem;
  padding: ${({ theme: { spacing } }) => `${spacing(5)} ${spacing(6)} ${spacing(6)}`};

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      width: ${PROFILE_PREVIEW_WIDTH};
      background-color: transparent;
      padding: 0;
    `,
    )}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  width: 100%;
  min-height: ${({ theme: { spacing } }) => spacing(7)};
  flex-shrink: 0;
`;

const PreviewFrame = styled.div`
  position: relative;
  width: ${PROFILE_PREVIEW_WIDTH};
  max-width: 100%;
  flex-shrink: 0;
`;

const PreviewImage = styled.img<{ $expanded?: boolean; $objectPosition?: string }>`
  display: block;
  width: 100%;
  height: ${({ $expanded }) => ($expanded ? PROFILE_PREVIEW_EXPANDED_HEIGHT : PROFILE_PREVIEW_COLLAPSED_HEIGHT)};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  object-fit: cover;
  object-position: ${({ $objectPosition }) => $objectPosition ?? 'top'};
  transition: height 0.3s ease;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      height: ${PROFILE_PREVIEW_EXPANDED_HEIGHT};
      border-radius: ${spacing(3)};
    `,
    )}
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.semantic.text_small_elements};
  align-self: center;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      display: none;
    `,
    )}
`;

const PreviewGradient = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  pointer-events: none;
  background: linear-gradient(180deg, transparent 59%, ${({ theme }) => theme.semantic.neutrals_100} 93%);

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      border-radius: ${spacing(3)};
    `,
    )}
`;

export const ProfileStep: React.FC = () => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const guideColors = getGuideColors(semantic);
  const [incompleteExpanded, setIncompleteExpanded] = useState(false);
  const [completeExpanded, setCompleteExpanded] = useState(false);

  const subtitle = intl.formatMessage({ id: 'leadsGuide.step2.subtitle' });
  const incompleteLabel = intl.formatMessage({ id: 'leadsGuide.step2.incomplete' });
  const completeLabel = intl.formatMessage({ id: 'leadsGuide.step2.complete' });
  const viewFullImageLabel = intl.formatMessage({ id: 'leadsGuide.step2.viewFullImage' });
  const viewLessLabel = intl.formatMessage({ id: 'leadsGuide.step2.viewLess' });

  return (
    <StepContent data-testid="leads-guide-step-profile">
      <Subtitle variant="h5">{subtitle}</Subtitle>
      <ComparisonRow>
        <ComparisonCard data-testid="leads-guide-profile-incomplete">
          <CardHeader>
            <CircleXCloseIcon
              size={20}
              backgroundColor={semantic.error_500}
              glyphColor={semantic.text_small_elements}
            />
            <Typography variant="h5" color={guideColors.textPrimary}>
              {incompleteLabel}
            </Typography>
          </CardHeader>
          <PreviewFrame>
            <PreviewImage src={ProfileIncompleteImage} alt="" $expanded={incompleteExpanded} $objectPosition="top" />
            {!incompleteExpanded && <PreviewGradient />}
          </PreviewFrame>
          <ExpandButton aria-expanded={incompleteExpanded} onClick={() => setIncompleteExpanded((v) => !v)}>
            <Typography variant="body3" color={guideColors.textSubtle}>
              {incompleteExpanded ? viewLessLabel : viewFullImageLabel}
            </Typography>
            {incompleteExpanded ? <ChevronUpSmall /> : <ChevronDownSmall />}
          </ExpandButton>
        </ComparisonCard>
        <ComparisonCard data-testid="leads-guide-profile-complete">
          <CardHeader>
            <CircleCheckIcon
              size={20}
              backgroundColor={semantic.success_400}
              glyphColor={semantic.text_small_elements}
            />
            <Typography variant="h5" color={guideColors.textPrimary}>
              {completeLabel}
            </Typography>
          </CardHeader>
          <PreviewFrame>
            <PreviewImage src={ProfileCompleteImage} alt="" $expanded={completeExpanded} $objectPosition="top" />
            {!completeExpanded && <PreviewGradient />}
          </PreviewFrame>
          <ExpandButton aria-expanded={completeExpanded} onClick={() => setCompleteExpanded((v) => !v)}>
            <Typography variant="body3" color={guideColors.textSubtle}>
              {completeExpanded ? viewLessLabel : viewFullImageLabel}
            </Typography>
            {completeExpanded ? <ChevronUpSmall /> : <ChevronDownSmall />}
          </ExpandButton>
        </ComparisonCard>
      </ComparisonRow>
    </StepContent>
  );
};
